import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Check, Lock, Sparkles, Heart } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { PROTECTED_PRESETS } from "../lib/constants";
import { Button } from "./ui/button";
import { toast } from "sonner";

const REVEAL_FIELDS = [
  { k: "region", label: "General area" },
  { k: "story", label: "Their story" },
  { k: "need", label: "What they need" },
  { k: "tags", label: "Tags" },
];

export default function VerifyReviewCard({ req, onApprove, onDecline, onOpenDoc }) {
  const [protect, setProtect] = useState(!!req.want_protected);
  const [alias, setAlias] = useState("");
  const [blurb, setBlurb] = useState("");
  const [reveal, setReveal] = useState({ region: true, story: true, need: true, tags: true });
  const [polishing, setPolishing] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleReveal = (k) => setReveal((r) => ({ ...r, [k]: !r[k] }));

  const polishBlurb = async () => {
    if (!blurb.trim()) { toast.error("Pick or write a note first."); return; }
    setPolishing(true);
    try {
      const { data } = await api.post("/ai/polish", { text: blurb, mode: "blurb" });
      if (data.text) { setBlurb(data.text); toast.success("Polished ✨"); }
    } catch (e) { toast.error(apiErr(e)); }
    setPolishing(false);
  };

  const approve = async () => {
    setBusy(true);
    await onApprove(req.id, {
      trusted: true,
      protected: protect,
      protected_alias: alias.trim() || "A neighbour under our wing",
      protected_blurb: blurb.trim(),
      protected_reveal: reveal,
    });
    setBusy(false);
  };

  return (
    <div data-testid={`verify-req-${req.id}`} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/u/${req.user_id}`} className="font-medium text-bronze hover:text-copper truncate inline-block">{req.name}</Link>
          {req.location && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {req.location}</p>}
          {req.want_protected && (
            <p className="text-xs font-semibold text-penny flex items-center gap-1 mt-1"><Lock className="w-3 h-3" /> Requested private identity ("Under Our Wing")</p>
          )}
          {req.note && <p className="text-sm text-foreground/85 mt-2 whitespace-pre-wrap break-words bg-secondary/40 rounded-xl px-3 py-2">{req.note}</p>}
          {req.protected_reason && (
            <div className="mt-2 bg-penny/5 border border-penny/20 rounded-xl px-3 py-2">
              <p className="text-[11px] font-mono uppercase tracking-wide text-penny mb-0.5">Private reason (admin-only)</p>
              <p className="text-sm text-foreground/85 whitespace-pre-wrap break-words">{req.protected_reason}</p>
            </div>
          )}
          {req.documents && req.documents.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid={`verify-docs-${req.id}`}>
              {req.documents.map((d, i) => (
                <button key={i} onClick={() => onOpenDoc(d.url)} data-testid={`verify-doc-${req.id}-${i}`}
                  className="text-xs text-copper underline bg-copper/5 border border-copper/20 rounded-lg px-2.5 py-1 hover:bg-copper/10">
                  📎 {d.label}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1.5">Requested {new Date(req.created_at).toLocaleString()}</p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button size="sm" data-testid={`verify-approve-${req.id}`} onClick={approve} disabled={busy} className="rounded-full bg-sage hover:bg-sage/90 text-white"><Check className="w-4 h-4 mr-1" /> Approve</Button>
          <Button size="sm" variant="outline" data-testid={`verify-decline-${req.id}`} onClick={() => onDecline(req.id)} className="rounded-full text-destructive border-destructive/40">Decline</Button>
        </div>
      </div>

      {/* Protected / Under Our Wing controls */}
      <div className="mt-3 pt-3 border-t border-border/60">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" data-testid={`verify-protect-toggle-${req.id}`} checked={protect} onChange={(e) => setProtect(e.target.checked)} className="accent-penny" />
          <span className="text-sm font-medium text-bronze flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-penny" fill="currentColor" /> Grant "Under Our Wing" (hide identity)</span>
        </label>

        {protect && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wide text-copper">Public alias (shown instead of name)</label>
              <input data-testid={`verify-alias-${req.id}`} value={alias} onChange={(e) => setAlias(e.target.value)}
                placeholder="e.g. A single mother of three" className="w-full rounded-xl border border-border px-3 py-2 text-sm bg-white mt-1" />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-mono uppercase tracking-wide text-copper">Why they're under our wing (public note)</label>
                <button type="button" data-testid={`verify-polish-blurb-${req.id}`} onClick={polishBlurb} disabled={polishing}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-copper hover:text-copper/80 disabled:opacity-50">
                  <Sparkles className="w-3.5 h-3.5" /> {polishing ? "Polishing…" : "Polish with AI"}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {PROTECTED_PRESETS.map((p, i) => (
                  <button key={i} type="button" data-testid={`verify-preset-${req.id}-${i}`} onClick={() => setBlurb(p)}
                    className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-secondary text-bronze hover:bg-penny/15">Preset {i + 1}</button>
                ))}
              </div>
              <textarea data-testid={`verify-blurb-${req.id}`} value={blurb} onChange={(e) => setBlurb(e.target.value)} rows={4}
                placeholder="Pick a warm preset above, polish with AI, or write your own…" className="w-full rounded-xl border border-border px-3 py-2 text-sm bg-white mt-1.5 resize-none" />
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-wide text-copper">What may we still show publicly?</label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {REVEAL_FIELDS.map((f) => (
                  <button key={f.k} type="button" data-testid={`verify-reveal-${req.id}-${f.k}`} onClick={() => toggleReveal(f.k)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border ${reveal[f.k] ? "bg-sage/15 border-sage/40 text-sage" : "bg-secondary border-border text-muted-foreground"}`}>
                    {reveal[f.k] ? "✓ " : ""}{f.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Their real name, face and exact address are always hidden. These toggles control the rest.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
